import React, { useEffect, useState } from "react";
import axios from "axios";
import { Table, Button, Modal, Input, Tooltip, Space } from "antd";
import DataTable from "react-data-table-component";
import config from "../../config";
import { FaTrash, FaEdit } from "react-icons/fa";
import { ToastContainer, toast, Bounce } from 'react-toastify';
import { useNavigate } from "react-router-dom";
import { SearchOutlined } from '@ant-design/icons';
import { FaChalkboardTeacher } from "react-icons/fa";
import Switch from "react-switch";
import SelectCourseModal from "../Forms/SelectCourseModal";
import PermissionGuard from "../Guards/PermissionGuard";
import { PERMISSIONS } from "../../constants/permissions";

axios.defaults.withCredentials = true;

const VideosTable = ({ type }) => {
    const [videos, setVideos] = useState([]);
    const [allVideos, setAllVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedVideo, setSelectedVideo] = useState(null);

    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);

    const [searchFilters, setSearchFilters] = useState({
        name: "",
        title: "",
        uniqueId: ""
    });
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const navigate = useNavigate();

    // Fetch videos from backend on mount
    useEffect(() => {
        fetchVideos();
    }, [navigate]);

    const fetchVideos = async (page = 1, pageSize = 10, filters = searchFilters) => {
        try {
            setLoading(true);

            let dynamicFilter = `FileType=${type}`;

            if (filters.name) {
                dynamicFilter += `${dynamicFilter ? "," : ""}name=*${filters.name}/i`;
            }

            if (filters.title) {
                dynamicFilter += `${dynamicFilter ? "," : ""}title=*${filters.title}/i`;
            }

            if (filters.uniqueId) {
                dynamicFilter += `${dynamicFilter ? "," : ""}uniqueId=*${filters.uniqueId}/i`;
            }
            
            const response = await axios.get(`${config.apiBaseUrl}/api/video/GetFilteredList`, {
                params: {
                    page,
                    pageSize,
                    filter: dynamicFilter,
                    orderBy: "uploadedAt desc"
                },
                headers: {
                    "HeaderKey": config.HeaderKey
                },
                withCredentials: true
            });
           
            setVideos(response.data.videos);

            setPagination({
                current: page,
                pageSize: pageSize,
                total: response.data.totalCount,
            });

        } catch (error) {
            toast.error("خطا در دریافت اطلاعات");
            if (error.response?.status === 401) {
                navigate("/");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSwitchChange = (id, newStatus) => {
        togglePublishStatus(id, newStatus);
    };
    const togglePublishStatus = async (id, newStatus) => {
        try {
            const response = await axios.post(
                `${config.apiBaseUrl}/api/video/TogglePublishStatus`,
                { id: id, completed: newStatus },
                {
                    headers: {
                        "Content-Type": "application/json",
                        "HeaderKey": config.HeaderKey,
                    },
                    withCredentials: true,
                }
            );

            if (response.status === 200) {
                setVideos(prevVideos =>
                    prevVideos.map(video =>
                        video.id === id ? { ...video, completed: newStatus } : video
                    )
                );
                toast.success(<p dir="rtl" className="font-custom">وضعیت فایل با موفقیت تغییر کرد!</p>, { autoClose: 3000 });
            }
            else {
                toast.error("خطا در تغییر وضعیت فایل!", { autoClose: 3000 });
            }
        } catch (error) {
            if (error.status === 414) {
                toast.error(<p dir="rtl" className="font-custom">{error.response.data.error}</p>, { autoClose: 3000 });
            } else {
                toast.error("خطا در برقراری ارتباط با سرور!", { autoClose: 3000 });
            }
        }
    };



    const handleSearchChange = (e, columnName) => {


        let value = e.target.value;

        const newFilters = {
            ...searchFilters,
            [columnName]: value
        };


        setSearchFilters(newFilters);
    };
    // Confirm delete dialog
    const handleDelete = (id) => {
        toast.warn(
            <div className="text-right font-custom">
                <p>آیا از حذف این فایل مطمئن هستید؟</p>
                <div className="flex justify-end gap-2 mt-2">
                    <button
                        className="bg-red-500 hover:bg-red-700 text-white px-3 py-1 rounded"
                        onClick={() => confirmDelete(id)}
                    >
                        حذف
                    </button>
                    <button
                        className="bg-gray-500 hover:bg-gray-700 text-white px-3 py-1 rounded"
                        onClick={() => toast.dismiss()}
                    >
                        انصراف
                    </button>
                </div>
            </div>,
            { autoClose: false }
        );
    };

    // Execute the delete if user confirms
    const confirmDelete = async (id) => {
        try {
            toast.dismiss();
            const response = await axios.post(
                `${config.apiBaseUrl}/api/video/DeleteVideo`,
                id,
                {
                    withCredentials: true,
                    headers: {
                        "Content-Type": "application/json",
                        "HeaderKey": config.HeaderKey
                    },
                }
            );

            if (response.status === 200) {
                setVideos((prev) => prev.filter(video => video.id !== id));
                toast.success(<p dir="rtl" className="font-custom">فایل با موفقیت حذف شد</p>);

            } else {
                toast.error("خطا در حذف فایل!", { autoClose: 3000 });
            }
        } catch (error) {
            toast.error(<p dir="rtl" className="font-custom">خطا در برقراری ارتباط</p>);
        }
    };

    // Example edit / completion screen
    const handleEdit = (video) => {
        navigate("/dashboard/videos/edit", { state: { video, type } });
    };


    const columns = [
        {
            title: "عملیات",
            key: "actions",
            fixed: "left",
            width: 20,
            align: "center",
            className: "font-custom text-center",
            render: (row) => (
                <div className="flex gap-2 justify-center">
                    <PermissionGuard permission={PERMISSIONS.CONTENTS_DELETE}>
                        <Button
                            type="link"
                            className="text-red-500"
                            onClick={() => handleDelete(row.id)}
                            icon={<FaTrash size={18} color="#980000" />}
                        />
                    </PermissionGuard>
                    <PermissionGuard permission={PERMISSIONS.CONTENTS_UPLOAD}>
                        <Button
                            type="link"
                            className="text-blue-500"
                            onClick={() => handleEdit(row)}
                            icon={<FaEdit size={18} />}
                        />
                    </PermissionGuard>
                    <PermissionGuard permission={PERMISSIONS.CONTENTS_PUBLISH}>
                        <Tooltip className="font-custom" title="انتخاب دوره">
                            <Button
                                type="link"
                                className="text-green-500"
                                onClick={() => {
                                    setSelectedVideo(row);
                                    setIsUpdateModalOpen(true);
                                }}
                                icon={<FaChalkboardTeacher size={18} color="#3aff00" />}
                            />
                        </Tooltip>
                    </PermissionGuard>
                </div>
            ),
        },

        {
            title: "وضعیت",
            key: "completed",
            width: 30,
            align: "center",
            className: "font-custom text-center",
            render: (row) => (
                <PermissionGuard
                    permission={PERMISSIONS.CONTENTS_PUBLISH}
                    fallback={
                        <span className="text-sm text-gray-600">
                            {row.completed ? "منتشر شده" : "منتشر نشده"}
                        </span>
                    }
                >
                    <Switch
                        onChange={() => handleSwitchChange(row.id, !row.completed)}
                        checked={row.completed}
                        onColor="#27ff00"
                        offColor="#ff1a00"
                        onHandleColor="#1c7510"
                        offHandleColor="#751010"
                        checkedIcon={
                            <div className="text-[10px] text-white pl-1 w-6 h-6 flex items-center justify-center text-center">
                            </div>
                        }
                        uncheckedIcon={
                            <div className="text-[10px] text-white pl-1 w-6 h-6 flex items-center justify-center text-center">

                            </div>
                        }
                        className="react-switch"
                        height={28}
                        width={50}
                    />
                </PermissionGuard>
            ),
        },
        {
            title: "سال تدریس",
            dataIndex: "createdDate",
            key: "createdDate",
            width: 30,
            align: "center",
            className: "font-custom text-center",
            render: (row) => {
                const parsedDate = new Date(row);
                const isValid = parsedDate instanceof Date && !isNaN(parsedDate);
                const isReasonable = isValid && parsedDate.getFullYear() >= 2000;

                if (!isReasonable) return "N/A";

                // Format in Persian (fa-IR) and extract only the year
                const formattedDate = parsedDate.toLocaleDateString("fa-IR", { year: "numeric" });
                return formattedDate;
            },
        },
        {
            title: "تاریخ آپلود",
            dataIndex: "uploadedAt",
            key: "uploadedAt",
            width: 30,
            align: "center",
            className: "font-custom text-center",
            render: (row) => new Date(row).toLocaleDateString("fa-IR", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: false,
            }),
        },
        {
            title: "شناسه یکتا",
            dataIndex: "uniqueId",
            key: "uniqueId",
            width: 20,
            align: "center",
            className: "font-custom text-center",
            filterDropdown: () => (
                <div style={{ padding: 8 }} className="font-custom">
                    <Space.Compact style={{ width: '100%' }}>
                        <Input
                            className="font-custom"
                            placeholder="جستجوی شناسه یکتا"
                            allowClear
                            onClear={() => {
                                const newFilters = {
                                    ...searchFilters,
                                    uniqueId: ""
                                };

                                setSearchFilters(newFilters);

                                setPagination(prev => ({
                                    ...prev,
                                    current: 1
                                }));

                                fetchVideos(1, pagination.pageSize, newFilters);

                            }}
                            value={searchFilters.uniqueId}
                            onChange={(e) => {
                                handleSearchChange(e, "uniqueId");
                            }}
                            onPressEnter={() => {
                                setPagination(prev => ({
                                    ...prev,
                                    current: 1
                                }));
                                fetchVideos(1, pagination.pageSize, searchFilters);
                            }}
                        />
                        <Button type="primary"
                            icon={<SearchOutlined />}
                            onClick={() => {
                                setPagination(prev => ({
                                    ...prev,
                                    current: 1
                                }));
                                // Server-side search when button is clicked
                                fetchVideos(1, pagination.pageSize, searchFilters);
                            }}
                        />
                    </Space.Compact>
                </div>
            ),
            onFilter: (value, record) => record.duration.toLowerCase().includes(value.toLowerCase()),
        },
        {
            title: "عنوان",
            dataIndex: "title",
            key: "title",
            width: 60,
            align: "right",
            className: "font-custom text-center",
            filterDropdown: () => (
                <div style={{ padding: 8 }} className="font-custom">
                    <Space.Compact style={{ width: '100%' }}>
                        <Input
                            className="font-custom"
                            placeholder="جستجوی عنوان"
                            allowClear
                            onClear={() => {
                                setPagination(prev => ({
                                    ...prev,
                                    current: 1
                                }));
                                const newFilters = {
                                    ...searchFilters,
                                    title: ""
                                };

                                setSearchFilters(newFilters);
                                fetchVideos(1, pagination.pageSize, newFilters);

                            }}
                            value={searchFilters.title}
                            onChange={(e) => {
                                handleSearchChange(e, "title");
                            }}
                            onPressEnter={() => {
                                setPagination(prev => ({
                                    ...prev,
                                    current: 1
                                }));
                                fetchVideos(1, pagination.pageSize, searchFilters);
                            }}
                        />
                        <Button type="primary"
                            icon={<SearchOutlined />}
                            onClick={() => {
                                setPagination(prev => ({
                                    ...prev,
                                    current: 1
                                }));
                                // Server-side search when button is clicked
                                fetchVideos(1, pagination.pageSize, searchFilters);
                            }}
                        />
                    </Space.Compact>
                </div>
            ),
            onFilter: (value, record) => record.title.toLowerCase().includes(value.toLowerCase()),
        },
        {
            title: "نام فایل",
            dataIndex: "name",
            key: "name",
            width: 40,
            align: "right",
            className: "font-custom text-center",
            filterDropdown: () => (

                <div style={{ padding: 8 }}>
                    <Space.Compact style={{ width: '100%' }}>
                        <Input
                            className="font-custom"
                            placeholder="جستجوی نام فایل"
                            allowClear
                            onClear={() => {
                                setPagination(prev => ({
                                    ...prev,
                                    current: 1
                                }));
                                const newFilters = {
                                    ...searchFilters,
                                    name: ""
                                };

                                setSearchFilters(newFilters);

                                fetchVideos(1, pagination.pageSize, newFilters);

                            }}
                            value={searchFilters.name}
                            onChange={(e) => {
                                handleSearchChange(e, "name");
                            }}
                            onPressEnter={() => {
                                setPagination(prev => ({
                                    ...prev,
                                    current: 1
                                }));
                                fetchVideos(1, pagination.pageSize, searchFilters);
                            }}
                        />
                        <Button type="primary"
                            icon={<SearchOutlined />}
                            onClick={() => {
                                setPagination(prev => ({
                                    ...prev,
                                    current: 1
                                }));
                                // Server-side search when button is clicked
                                fetchVideos(1, pagination.pageSize, searchFilters);
                            }}
                        />
                    </Space.Compact>
                </div>
            ),
            onFilter: (value, record) => record.name.toLowerCase().includes(value.toLowerCase()),
        },
    ];

    // Conditionally color rows based on `completed`
    const conditionalRowStyles = [
        {
            when: row => row.completed,
            style: {
                backgroundColor: 'rgba(0, 128, 0, 0.1)'  // light green
            },
        },
        {
            when: row => !row.completed,
            style: {
                backgroundColor: 'rgba(128, 128, 128, 0.1)' // light gray
            },
        },
    ];

    return (
        <div className="font-custom rtl text-right p-5">
            <h1 className="text-center text-xl mt-5 mb-5">
                {type === 0
                    ? "لیست ویدیوهای سامانه"
                    : type === 1
                        ? "لیست پی‌دی‌اف های سامانه"
                        : "لیست محتوای صوتی سامانه"}
            </h1>


            <PermissionGuard permission={PERMISSIONS.CONTENTS_UPLOAD}>
                <button
                    className="bg-green-700 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-full shadow-md transition duration-300 mb-5"
                    onClick={() => navigate("/dashboard/gallery")}
                >
                    + آپلود فایل جدید
                </button>
            </PermissionGuard>

            <div className="w-full shadow-lg">
                <Table
                    columns={columns}
                    dataSource={videos}
                    loading={loading}
                    rowKey={(record) => record.id}
                    scroll={{ x: 1000 }}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        onChange: (page, pageSize) => {
                            setPagination(prev => ({
                                ...prev,
                                current: page,
                                pageSize
                            }));

                            fetchVideos(page, pageSize, searchFilters);
                        },
                    }}
                    bordered
                />
                <Modal
                    open={isUpdateModalOpen}
                    onCancel={() => setIsUpdateModalOpen(false)}
                    footer={null}
                    centered
                    width={1000}
                    destroyOnClose
                >
                    <SelectCourseModal
                        object={selectedVideo}
                        onSuccess={() => {
                            setIsUpdateModalOpen(false);
                            fetchVideos(); // refresh users after update
                        }}
                        type="video"
                    />
                </Modal>
            </div>

            <ToastContainer
                position="bottom-center"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                className={"font-custom text-center rtl"}
                pauseOnHover
                theme="dark"
                transition={Bounce}
            />
        </div>
    );
};

export default VideosTable;
